//  @flow
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box, Container, Grid, Divider } from "@mui/material";
import { Notification } from "components/Atoms";

import * as Actions from "actions/user/user.tryonpackages";
import * as BaseActions from "actions/user/user-base.actions";
import * as ActionTypes from 'constants/ActionTypes';

import TryOnPackageCard from "components/elements/user/TryOnPackageCard";
import TryOnOrderDetail from "components/elements/user/TryOnOrderDetail";
import ShopHeaderBack from "components/elements/main/ShopHeaderBack";
import CustomSectionCreator from "components/CustomSectionCreator";
import MockGeneralHeader from "components/elements/main/MockGeneralHeader";
import ButtonComponent from "components/Button";
import { H2, H4 } from "components/CustomTypography";
import { getLocalDate, getPriceCurrency, getURI } from "helpers/utils";
import { DATE_FORMAT_SHORT } from "config/constants";

import "./styles.scss";

const TryOnPackageStore = (props) => {
  const [viewMode, setViewMode] = useState("PACKAGES"); // PACKAGES, ORDERS, ORDER_DETAIL
  const [selectedOrder, setSelectedOrder] = useState(null);

  const history = useHistory();

  const {
    serviceError,
    reqStatus,
    isLoading,
    currency,
    packages,
    orders,
    orderDetail,
    paymentOrder,
    paymentVerification,
    tryonCredits,
    getTryOnPackages,
    getTryOnOrders,
    getTryOnOrderDetail,
    createTryOnPaymentOrder,
    verifyTryOnPayment,
    getTryOnCredits,
  } = props;

  useEffect(() => {
    getTryOnPackages();
    getTryOnOrders({ limit: 20, skip: 0 });
    getTryOnCredits();
  }, []);

  const sendNotification = (type, { title, description = "" }) => {
    Notification[type]({
      message: title,
      description,
    });
  };

  useEffect(() => {
    if (serviceError) {
      const feedback = {
        title: "Something went wrong!",
        description: serviceError.statusText || serviceError,
      };
      return sendNotification("error", feedback);
    }

    switch (reqStatus) {
      case ActionTypes.CREATE_TRYON_PAYMENT_ORDER_SUCCESS:
        if (paymentOrder && paymentOrder.data) {
          handlePaymentProcessing(paymentOrder.data);
        }
        break;
      case ActionTypes.VERIFY_TRYON_PAYMENT_SUCCESS:
        sendNotification("success", {
          title: "Payment Successful!",
          description: "Credits have been added to your account.",
        });
        getTryOnPackages();
        getTryOnOrders({ limit: 20, skip: 0 });
        getTryOnCredits();
        setViewMode("PACKAGES");
        break;
      default:
        break;
    }
  }, [serviceError, reqStatus]);

  const handlePackageSelect = (pkg) => {
    history.push({
      pathname: getURI(process.env.REACT_APP_TYPE, "credits_checkout", ""),
      state: { selectedPackage: pkg },
    });
  };

  const handleOrderDetail = (order) => {
    setSelectedOrder(order);
    setViewMode("ORDER_DETAIL");
    getTryOnOrderDetail(order._id);
  };

  const handlePayment = () => {
    if (orderDetail && orderDetail._id) {
      createTryOnPaymentOrder({ order_id: orderDetail._id });
    }
  };

  const handlePaymentVerification = (response) => {
    verifyTryOnPayment({
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
      order_id: orderDetail.order_id,
    });
  };

  const handlePaymentProcessing = (data) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: data.amount,
      currency: "INR",
      name: "Shopana",
      image: "https://shopana.in/images/shopanaicon.png",
      description: "Try-On Credits Package",
      order_id: data.id,
      handler: function(response) {
        handlePaymentVerification(response);
      },
      theme: {
        color: "#FFFFFF",
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  const renderPackages = () => (
    <Container>
      <Box className="credits-display-banner" mb={3} mt={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
          <Box>
            <H4 fontSize={14} color="textSecondary">Available Credits</H4>
            <H2 fontSize={32} fontWeight="bold" color="primary">
              {tryonCredits ? tryonCredits.balance || 0 : 0}
            </H2>
          </Box>
        </Box>
      </Box>

      <div id="packages-section">
        <CustomSectionCreator isSubtle={true} title="Buy Shopana Credits" />
      </div>
      {isLoading ? (
        <MockGeneralHeader />
      ) : (
        <Grid container spacing={3}>
          {packages && packages.map((pkg, index) => (
            <Grid item xs={12} sm={6} md={4} key={pkg._id || index}>
              <TryOnPackageCard
                currency={currency}
                data={pkg}
                onSelect={handlePackageSelect}
              />
              <Divider />
            </Grid>
          ))}
        </Grid>
      )}

      <Box pb={4} />

      <CustomSectionCreator isSubtle={true} title="Order History" />
      {isLoading ? (
        <MockGeneralHeader />
      ) : orders && orders.length > 0 ? (
        <>
          {orders.slice(0, 5).map((order, index) => (
            <Box
              key={order._id || index}
              className="styled-card"
              width="100%"
              display="flex"
              mt={2}
              mb={2}
              onClick={() => handleOrderDetail(order)}
              sx={{ cursor: 'pointer' }}
            >
              <Box width="50%" order={1} ml={2}>
                <H2 mb={1} fontSize={16} sx={{ textDecoration: 'underline' }}>
                  Order# {order.order_id}
                </H2>
                <H4 fontSize={12}>
                  {getLocalDate(order.created_at, DATE_FORMAT_SHORT)}
                </H4>
                <H4 fontSize={12}>
                  {order.credits} Credits
                </H4>
              </Box>
              <Box width="30%" order={2} ml={2}>
                <H2 mb={1} fontSize={16}>
                  {getPriceCurrency(order.pricing?.total, currency)}
                </H2>
              </Box>
              <Box width="20%" order={3} ml={2}>
                <ButtonComponent
                  type="primary"
                  variant={order.payment_status === 'paid' ? 'outlined' : 'contained'}
                  name={order.payment_status === 'paid' ? 'View' : 'Pay'}
                  size="medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOrderDetail(order);
                  }}
                />
              </Box>
            </Box>
          ))}
          {orders.length > 5 && (
            <Box mt={2}>
              <ButtonComponent
                type="primary"
                variant="outlined"
                name="View All Orders"
                size="medium"
                onClick={() => setViewMode("ORDERS")}
              />
            </Box>
          )}
        </>
      ) : (
        <Box>
          <H4>No previous orders</H4>
        </Box>
      )}
    </Container>
  );

  const renderAllOrders = () => (
    <>
      <ShopHeaderBack onCancel={() => setViewMode("PACKAGES")} history={history} />
      <CustomSectionCreator isSubtle={true} title="All Orders" />
      <Container>
        {orders && orders.map((order, index) => (
          <Box
            key={order._id || index}
            className="styled-card"
            width="100%"
            display="flex"
            mt={2}
            mb={2}
            onClick={() => handleOrderDetail(order)}
            sx={{ cursor: 'pointer' }}
          >
            <Box width="50%" order={1} ml={2}>
              <H2 mb={1} fontSize={16} sx={{ textDecoration: 'underline' }}>
                Order# {order.order_id}
              </H2>
              <H4 fontSize={12}>
                {getLocalDate(order.created_at, DATE_FORMAT_SHORT)}
              </H4>
              <H4 fontSize={12}>
                {order.credits} Credits
              </H4>
            </Box>
            <Box width="30%" order={2} ml={2}>
              <H2 mb={1} fontSize={16}>
                {getPriceCurrency(order.pricing?.total, currency)}
              </H2>
            </Box>
            <Box width="20%" order={3} ml={2}>
              <ButtonComponent
                type="primary"
                variant={order.payment_status === 'paid' ? 'outlined' : 'contained'}
                name={order.payment_status === 'paid' ? 'View' : 'Pay'}
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderDetail(order);
                }}
              />
            </Box>
          </Box>
        ))}
      </Container>
    </>
  );

  const renderOrderDetailPage = () => (
    <Container>
      <TryOnOrderDetail
        isLoadingBootInfo={isLoading}
        currency={currency}
        orderDetail={selectedOrder}
        handlePayment={handlePayment}
        onCancel={() => setViewMode("PACKAGES")}
      />
    </Container>
  );

  const renderView = () => {
    switch (viewMode) {
      case "ORDER_DETAIL":
        return renderOrderDetailPage();
      case "ORDERS":
        return renderAllOrders();
      case "PACKAGES":
      default:
        return renderPackages();
    }
  };

  return (
    <Container>
      {renderView()}
    </Container>
  );
};

const mapStateToProps = ({ tryonPackages, bootupInfo, userbootupInfo }) => ({
  bootupInfo,
  currency: bootupInfo.symbol,
  reqStatus: tryonPackages.reqStatus,
  isLoading: tryonPackages.isLoading,
  serviceError: tryonPackages.serviceError,
  packages: tryonPackages.packages,
  orders: tryonPackages.orders,
  orderDetail: tryonPackages.orderDetail,
  paymentOrder: tryonPackages.paymentOrder,
  paymentVerification: tryonPackages.paymentVerification,
  tryonCredits: userbootupInfo.tryonCredits,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...Actions, ...BaseActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TryOnPackageStore);
